/**
 * FormP — the pricing vocabulary, shared by both frontends.
 *
 * Sibling of `shared/permissions/constants/formA.js`, and deliberately the same shape:
 * a closed vocabulary plus the labels that name it, held to the backend by
 * `backend/tests/test_pricing_vocabulary_parity.py`.
 *
 * THIS FILE DOES NOT FORMAT MONEY, and that absence is the design.
 *
 * Every price a person reads is rendered by `backend/app/authz/pricing.py:render()` and
 * arrives pre-formatted as `{headline, alternate, detail}` — on the consent preview, on
 * each app in the store listing, and in `paradigm pricing preview`. A currency
 * formatter here would be a second renderer, and two renderers drift: one of them
 * learns that JPY has no decimal places, or that trailing `.00` should be dropped, or
 * that an annual saving is worth naming, and the other doesn't. Then the store badge
 * and the consent card disagree about what an app costs, which is the one thing in this
 * system that must never be ambiguous.
 *
 * So what IS here is the *authoring* half — what the developer portal needs to build a
 * pricing object and validate it before staging:
 *
 *   PRICING_MODELS     which models exist
 *   MODEL_LABELS       what to call them in a picker
 *   MODEL_DESCRIPTIONS what each one means, for the form
 *   FIELDS_BY_MODEL    which fields the form shows, and their bounds
 *   CURRENCIES         the supported set, mirroring the backend's
 *
 * Display is the backend's. Authoring is ours. Neither does the other's job.
 */

/** The models an app may charge on. Mirrors PRICING_MODELS in app/authz/pricing.py. */
export const PRICING_MODELS = ['free', 'one_time', 'subscription'];

/**
 * Designed, not yet built. A UI must not offer these — and must not silently treat an
 * app already carrying one as free. Show it as unavailable and say so.
 *
 * `metered` is the deferred fourth: the app reports units it performed, the platform
 * counts them against a ceiling the user consented to. It is last because it is the
 * only model that reaches into the runtime request path, and the only one where Stripe
 * cannot enforce the cap for us.
 */
export const RESERVED_MODELS = ['metered'];

export const MODEL_LABELS = {
  free: 'Free',
  one_time: 'One-time payment',
  subscription: 'Subscription',
  metered: 'Pay per use (coming soon)',
};

export const MODEL_DESCRIPTIONS = {
  free: 'Anyone who authorizes your app gets access. No payout account needed.',
  one_time: 'People pay once and keep access permanently.',
  subscription: 'People pay monthly or annually. You can offer a free trial.',
  metered: 'You report usage and people pay for what they use, up to a cap they agree to.',
};

/**
 * The fields each model takes, in the order a form should show them.
 *
 * `minor: true` means the input is money and is stored in MINOR UNITS — the form shows
 * £12.00 and sends 1200. The conversion belongs at the input boundary, once, and never
 * in a display path: nothing downstream of here ever turns 1200 back into "£12.00"
 * except the backend renderer.
 *
 * Bounds are advisory here and authoritative in the backend validator. They exist so
 * the form can refuse locally with a useful message instead of round-tripping to
 * discover that 30p is the floor — the same reason `paradigm validate --offline` exists.
 */
export const FIELDS_BY_MODEL = {
  free: [],
  one_time: [
    { key: 'currency', type: 'currency', required: true, label: 'Currency' },
    { key: 'amount', type: 'money', minor: true, required: true, label: 'Price' },
  ],
  subscription: [
    { key: 'currency', type: 'currency', required: true, label: 'Currency' },
    { key: 'monthly', type: 'money', minor: true, required: false, label: 'Monthly price' },
    { key: 'annual', type: 'money', minor: true, required: false, label: 'Annual price' },
    { key: 'trial_days', type: 'integer', required: false, label: 'Free trial (days)', max: 730 },
  ],
};

/**
 * Supported currencies. `decimals` and `min_charge` mirror app/authz/pricing.py, which
 * is the enforcement point; they are here so the form's money input knows how many
 * decimal places to accept and what floor to warn at.
 *
 * `min_charge` is Stripe's per-currency minimum. Below it a charge fails at Stripe with
 * an error naming no field, so catching it at authoring time is the difference between
 * a clear message and a support ticket.
 */
export const CURRENCIES = {
  GBP: { symbol: '£', decimals: 2, min_charge: 30 },
  USD: { symbol: '$', decimals: 2, min_charge: 50 },
  EUR: { symbol: '€', decimals: 2, min_charge: 50 },
};

/** The zero value. A new app is free, and an app with no pricing is free. */
export const FREE = { model: 'free' };

/**
 * Whether a pricing blob charges. The frontend's one legitimate question about a price
 * it did not get a rendering for — used to decide whether to show a payout-account
 * warning, or whether the consent flow needs a checkout step.
 */
export function isPaid(pricing) {
  return Boolean(pricing) && (pricing.model || 'free') !== 'free';
}

/**
 * Whether this blob can be offered at all. An app carrying a reserved or unknown model
 * FAILS CLOSED — it cannot be authorized, and a UI must say that rather than render it
 * as free. A price that is charged but never displayed is the failure this whole
 * vocabulary exists to prevent.
 */
export function isOfferable(pricing) {
  const model = (pricing && pricing.model) || 'free';
  return PRICING_MODELS.includes(model);
}
